import { history } from 'syft-acp-core/history'
import moment from 'moment'

import { formatLocalDatetime } from 'syft-acp-util/time'
import { filterAllocationName } from 'syft-acp-core/lib/shifts'

import { branchAccountsPlaceholder } from './ShiftAgencyList'
import { ShiftAgencyListProps as Props, SubRowData, RowData } from './ShiftAgencyList.types'
import { AgencyShiftEntity } from 'syft-acp-core/store/agency-shifts/types'
import {
  fetchAgencyUnfilledAllocations,
  bulkDeleteAgencyUnfilledAllocations,
} from 'syft-acp-core/store/agency-unfilled-allocations/actions'

export const handleDisabledTooltip = (_rowData?: RowData, subRowData?: SubRowData) => {
  if (subRowData?.allocation_type !== 'AgencyShift') {
    return 'Shift must be allocated to agency first.'
  }
  if (!subRowData?.workers_requested) {
    return 'There are no more spots available to cascade.'
  }

  return 'Disabled'
}

/** Formats the allocation date as a regular timestamp. */
export const formatAllocationDate = (subRowData?: SubRowData) => {
  const date = subRowData?.sent_to_agency_at

  return date != null ? formatLocalDatetime(date, { showTimezone: true }).abs : null
}

/** Formats the allocation date as a relative string (e.g. "3h ago"). */
export const formatSentToAgencyRelative = (subRowData?: SubRowData) => {
  const date = subRowData?.sent_to_agency_at

  return date != null ? formatLocalDatetime(date, { showRelative: true, relativeOnly: true }).formatted : null
}

const convertNullishToDash = (value: any) => (value == null ? '-' : value)

export const formatProvidedRequestedWorkers = (subRowData?: SubRowData) => {
  const hasBuffer = subRowData?.reported_demand_buffer != null && subRowData.reported_demand_buffer > 0
  const denominator = subRowData?.allocation_without_buffer ?? subRowData?.workers_requested
  const workersRequested = convertNullishToDash(denominator)

  if (hasBuffer) {
    const demandProvided = convertNullishToDash(
      subRowData?.workers_provided != null
        ? subRowData.workers_provided - (subRowData?.workers_provided_in_buffer ?? 0)
        : null,
    )

    return `${demandProvided}/${workersRequested}`
  }

  const workersProvided = convertNullishToDash(subRowData?.workers_provided)

  return `${workersProvided}/${workersRequested}`
}

export const isPast = (date: Date | string) => {
  const today = moment()
  const startDate = moment(date)

  return startDate.isBefore(today)
}

export const selectHighlight = (rowData: RowData) => rowData.highlight

export const selectRowLink = (rowData: RowData) =>
  `/listings/view/${rowData.listing_id}/job/${rowData.job_id}/shift/${rowData.id}`

export const selectWorkersValue = (rowData: RowData) =>
  `${rowData.workers_booked}/${rowData.workers_required}`

export const selectBookedVsDemand = (rowData: RowData) => {
  const agencyBuffer = rowData.agency_reported_demand_buffer ?? 0
  const bufferBooked =
    rowData.allocations?.reduce((sum, a) => sum + (a.workers_provided_in_buffer ?? 0), 0) ?? 0
  const demand = rowData.workers_required - agencyBuffer
  // Note: demandBooked can be negative if bufferBooked exceeds workers_booked. This is intentional —
  // negative values surface upstream data integrity issues rather than masking them.
  const demandBooked = Math.min(rowData.workers_booked - bufferBooked, demand)

  return `${demandBooked}/${demand}`
}

export const hasAnyBuffer = (data: { data: AgencyShiftEntity[] } | AgencyShiftEntity[]) => {
  const items = Array.isArray(data) ? data : data?.data

  return (
    Array.isArray(items) &&
    items.some(shift =>
      shift.allocations?.some(a => a.reported_demand_buffer != null && a.reported_demand_buffer > 0),
    )
  )
}

export const selectBufferLimit = (_: RowData, subRowData: SubRowData) => {
  const buffer = subRowData?.reported_demand_buffer
  if (buffer == null || buffer === 0) return '-'
  const provided = convertNullishToDash(subRowData?.workers_provided_in_buffer)

  return `${provided}/${buffer}`
}

export const selectAgency = (_: RowData, subRowData: SubRowData) => filterAllocationName(subRowData)

export const selectSentToAgencyTooltip = (_: RowData, subRowData?: SubRowData) => {
  const allocationDate = formatAllocationDate(subRowData)

  return typeof subRowData?.agency_cascade_scheduling_plan_id === 'number' && allocationDate
    ? `Agency accepted at: ${allocationDate}`
    : allocationDate
}

export const selectRequiredRequested = (_: RowData, subRowData: SubRowData) =>
  formatProvidedRequestedWorkers(subRowData)

export const selectBranchAccounts = (_: RowData, subRowData: SubRowData) =>
  branchAccountsPlaceholder(subRowData)

export const handleDropdownCascadeShifts = ({
  data: { start_time, agency_id, job_id, allocation_id },
  fullJob,
  action,
  refetch,
}: {
  fullJob: boolean
  action: (...args: Parameters<typeof fetchAgencyUnfilledAllocations>) => void
  data: { start_time: string; agency_id: number; job_id: number; allocation_id: number }
  refetch: () => void
}) => {
  if (!isPast(start_time)) {
    action(
      String(agency_id),
      {
        job_id,
        id: String(agency_id),
        allocation_id,
      },
      fullJob,
      refetch,
    )
  }
}

export const handleBulkDeleteUnfilledAllocations = ({
  data: { ids },
  action,
  refetch,
}: {
  action: (...args: Parameters<typeof bulkDeleteAgencyUnfilledAllocations>) => void
  data: { ids: number[] }
  refetch: () => void
}) => {
  action(ids, refetch)
}

/**
 * Whether we can plausibly place a fulfilment call for this allocation.
 *
 * The Agency Shifts list payload carries no phone numbers directly — only the `agency_accounts`
 * display string. Crucially, the AgencyCallModal does the authoritative contact resolution: it can
 * pull the agency head-office number (from agencyProfile) and branch/account numbers, AND it ALWAYS
 * offers a free-type number field. So the action is callable for ANY agency allocation — keying the
 * gate on the `agency_accounts` placeholder (its old behaviour) wrongly disabled agencies that have
 * a head-office number or that Ops could simply dial manually.
 *
 * We therefore only disable the action for the genuinely-uncallable case: an allocation that isn't
 * an agency shift (there is no agency to call).
 */
export const hasResolvableContact = (subRowData?: SubRowData): boolean =>
  subRowData?.allocation_type === 'AgencyShift'

export const NO_CONTACT_TOOLTIP = 'Shift must be allocated to an agency before you can call for fulfilment.'

/**
 * Options that gate / wire up the optional 5th "Call agency for fulfilment" action
 * (STSMPD-1321, behind the `agency_fulfilment_call_bot` flag).
 */
export type DropdownExtraOptions = {
  /** Flag value — when false the action is omitted entirely. */
  showCallAction?: boolean
  /** Opens the AgencyCallModal with the row/sub-row context. */
  onCallAgency?: (rowData: RowData, subRowData: SubRowData) => void
}

export const getDropdownOptions = (
  actions: Props['actions'],
  refetch: () => void,
  onActionDropdownChanged: (
    key: string,
    {
      agency_id,
      allocation_id,
      job_id,
      start_time,
    }: {
      agency_id?: number
      allocation_id?: number
      job_id?: number
      start_time?: string
      id?: number
    },
  ) => void,
  extraOptions: DropdownExtraOptions = {},
) => [
  {
    label: 'View history',
    action: (data: RowData) => () => {
      onActionDropdownChanged('view_history', { id: data.id })
      history.push(`/shifts/agency-shift-history?shift_id=${data.id}`)
    },
  },
  {
    label: 'Cascade shift',
    action:
      // @ts-expect-error


        ({ job_id, start_time }, { agency_id, allocation_id }) =>
        async () => {
          onActionDropdownChanged('cascade_shift', { agency_id, allocation_id, job_id, start_time })

          return handleDropdownCascadeShifts({
            data: { agency_id, allocation_id, job_id, start_time },
            fullJob: false,
            action: actions.fetchAgencyUnfilledAllocations,
            refetch,
          })
        },
    disabled: (data: RowData) => isPast(data?.start_time),
    disabledTooltip: 'You can not cascade shifts in the past',
  },
  {
    label: 'Cascade full job',
    action:
      // @ts-expect-error


        ({ job_id, start_time }, { agency_id, allocation_id }) =>
        async () => {
          onActionDropdownChanged('cascade_full_job', { agency_id, allocation_id, job_id, start_time })

          return handleDropdownCascadeShifts({
            data: { agency_id, allocation_id, job_id, start_time },
            fullJob: true,
            action: actions.fetchAgencyUnfilledAllocations,
            refetch,
          })
        },
    // @ts-expect-error
    disabled: data => isPast(data?.start_time),
    disabledTooltip: 'You can not cascade shifts in the past',
  },
  {
    label: 'Delete unfilled allocations',
    action:
      // @ts-expect-error


        ({ job_id, start_time }, { agency_id, allocation_id }) =>
        async () => {
          onActionDropdownChanged('delete_unfilled_allocations', {
            agency_id,
            allocation_id,
            job_id,
            start_time,
          })

          return handleBulkDeleteUnfilledAllocations({
            data: { ids: [allocation_id] },
            action: actions.bulkDeleteAgencyUnfilledAllocations,
            refetch,
          })
        },
  },
  ...(extraOptions.showCallAction
    ? [
        {
          label: 'Call agency for fulfilment',
          action: (rowData: RowData, subRowData: SubRowData) => () => {
            onActionDropdownChanged('call_agency_for_fulfilment', {
              agency_id: subRowData?.agency_id,
              allocation_id: subRowData?.allocation_id,
              job_id: rowData?.job_id,
              start_time: rowData?.start_time,
            })
            extraOptions.onCallAgency?.(rowData, subRowData)
          },
          disabled: (_rowData: RowData, subRowData: SubRowData) => !hasResolvableContact(subRowData),
          disabledTooltip: NO_CONTACT_TOOLTIP,
        },
      ]
    : []),
]
